// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Users } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useOrg } from "@/contexts/useOrg";
import AxiosInstance from "@/utils/AxiosInstance";
import { PhoneInput } from "@/components/ui/input-phone";
import { useAuth } from "@/contexts/use-auth";
import { useGetData } from "@/hooks/useGetData";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import {
  capitalizeTheStartingCharacterFilter,
  PERSON_CAPITALIZE_FIELDS,
} from "@/constants/payloadFilters";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfPhone from "@/components/rhfFields/RhfPhone";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RHFWrapper from "@/components/RHFWrapper";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormContext, useWatch } from "react-hook-form";
import { patientValidationSchema } from "@/utils/ValidataionSchemas";
import chronicConditions from "@/utils/ChronicQuestions.json";
import LoaderWrapper from "@/pages/LoaderWrapper";
import RhfAsyncSelect from "@/components/rhfFields/RhfAsyncSelect";
import CityStateZipcode from "@/components/CityStateZipcode";
import RhfBatchedInput from "@/components/rhfFields/RhfBatchedInputs";
import Grid from "@/components/Grid";
import RhfAddressAutoComplete from "@/components/rhfFields/RhfAddressAutoComplete";
import CustomDatepicker from "@/components/customeDatepicker";
import RhfCustomDatepicker from "@/components/rhfFields/RhfCustomDatepicker";

function DateOfBirthField() {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name="dateOfBirth"
      render={({ field }) => {
        const val = field.value;
        const ymd =
          typeof val === "string" && val
            ? val.includes("T")
              ? val.split("T")[0]
              : val.slice(0, 10)
            : "";
        return (
          <FormItem>
            <FormLabel>Date of Birth *</FormLabel>
            <FormControl>
              <CustomDatepicker
                value={ymd}
                onChange={(v) => {
                  field.onChange(v ? `${v}T00:00:00.000Z` : "");
                }}
                placeholder="Select date of birth"
                maxDate={new Date()}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

// Temporary data for UI - Replace with API calls later
const TEMP_PROVIDERS = [
  {
    _id: "provider1",
    name: "Dr. Sarah Johnson",
    licenseNumber: "MD-12345",
    organizationId: "clinic-123",
  },
  {
    _id: "provider2",
    name: "Dr. Michael Chen",
    licenseNumber: "MD-67890",
    organizationId: "org1",
  },
  {
    _id: "provider3",
    name: "Dr. Emily Rodriguez",
    licenseNumber: "MD-24680",
    organizationId: "anthro-care-clinic",
  },
  {
    _id: "provider4",
    name: "Dr. James Wilson",
    licenseNumber: "MD-13579",
    organizationId: "anthro-care-clinic",
  },
];

const TEMP_CARETAKERS = [
  {
    _id: "caretaker1",
    firstName: "Anna",
    lastName: "Martinez",
    assignedOrganizations: ["clinic-123"],
  },
  {
    _id: "caretaker2",
    firstName: "John",
    lastName: "Davis",
    assignedOrganizations: ["clinic-123", "anthro-care-clinic"],
  },
  {
    _id: "caretaker3",
    firstName: "Lisa",
    lastName: "Thompson",
    assignedOrganizations: ["anthro-care-clinic"],
  },
];

const CCM_DIAGNOSES = chronicConditions.map((data) => data.diseaseName);

export default function AddPatient({ isEdit }) {
  const navigate = useNavigate();
  const [selectedOrgForAdd, setSelectedOrgForAdd] =
    useState<Id<"organizations"> | null>(null);
  const { organizations, isAllSelected } = useOrg();
  const { patientId } = useParams();
  const [btnLoading, setBtnLoading] = useState(false);
  console.log({ patientId, isEdit });
  const { user, isAuthorized } = useAuth();
  const [patient, setPatient] = useState({ status: "pending" });
  const { loading: patientLoading, err: patientErr } = useGetData(
    "/patients/" + patientId,
    {},
    false,
    !!patientId && isEdit && organizations.length > 0,
    (err, data) => {
      if (data && !err) {
        const d = data?.data;
        setPatient({
          ...d,
          primaryNumber: d?.primaryNumber ?? "",
          secondaryNumber: d?.secondaryNumber ?? "",
          landlineNumber: d?.landlineNumber ?? "",
          organizationId: d?.organizationId?._id || d?.organizationId,
          assignedProviderId: d?.assignedProviderId?._id,
          assignedCaretakerIds: (d?.assignedCaretakerIds || []).map(
            (i) => i?._id,
          ),
          assignedPractitionerId: d?.assignedPractitionerId?._id,
          facilityId: d?.facilityId?._id || d?.facilityId,
          facilityRoom: d?.facilityRoom,
          bedLocation: d?.bedLocation,
          facilityUnit: d?.facilityUnit,
          height_feet: d?.heightValue?.split("'")?.[0]?.trim() || "",
          height_inch: (d?.heightValue?.split("'")?.[1] || "")?.split('"')?.[0]?.trim() || "",
          // Normalize address fields so form validation never gets undefined (avoids Update Patient error when state already has value)
          state: (d?.state ?? d?.address?.state ?? "").toString().trim(),
          city: (d?.city ?? d?.address?.city ?? "").toString().trim(),
          zipCode: (d?.zipCode ?? d?.zipcode ?? d?.address?.zipCode ?? d?.address?.zipcode ?? "").toString().trim().replace(/\D/g, ""),
        });
      }
    },
  );
  const [selectedFacilityForAdd, setSelectedFacilityForAdd] =
    useState<Id<"facilities"> | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    organizationId: "",
    assignedProviderId: "",
    assignedPractitionerId: "",
    assignedCaretakerId: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    heightValue: "",
    weightValue: "",
    typeOfPatient: "",
    status: "active",
    email: "",
    primaryNumber: "",
    secondaryNumber: "",
    landlineNumber: "",
    landlineExtension: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    primaryInsuranceName: "",
    primaryInsuranceNumber: "",
    secondaryInsuranceName: "",
    secondaryInsuranceNumber: "",
    dx1: "",
    dx2: "",
    dx3: "",
    dx4: "",
    dx5: "",
    allergies: "",
    programs: "",
  });
    console.log("formData", formData);
  // TODO: Replace with API call to fetch providers
  // const providers = await fetch('/api/providers').then(res => res.json());

  const handleInputChange = (name: string, value: string) => {
    // console.log("name", name, "value", value);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreatePatient = async (formdata: any) => {

    console.log("formdata", formdata);
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      const typeOfPatient = formdata?.typeOfPatient;
      let modifiedPayload: any = {
        ...formdata,
        heightValue: `${formdata?.height_feet || ""}' ${formdata?.height_inch || ""}"`,
      };
      // Outpatient: backend ko facilityId/facility fields mat bhejo – isse "Error in form fields facilityId" nahi aayega
      if (typeOfPatient === "outpatient") {
        delete modifiedPayload.facilityId;
        delete modifiedPayload.facilityRoom;
        delete modifiedPayload.facilityUnit;
        delete modifiedPayload.bedLocation;
      }
      setBtnLoading(true);
      console.log( formdata);
      modifiedPayload = capitalizeTheStartingCharacterFilter(
        modifiedPayload,
        PERSON_CAPITALIZE_FIELDS,
      );
      if (isEdit) {
        const { data } = await AxiosInstance().put(
          "/patients/" + patientId,
          modifiedPayload,
        );

        // toast.success();
        resolveFn("Patient updated successfully");
        navigate(-1);
      } else {
        const { data } = await AxiosInstance().post(
          "/patients",
          modifiedPayload,
        );

        // toast.success();
        resolveFn("Patient added successfully");
        navigate("/patients");
      }
    } catch (error) {
      rejectFn(error);
      console.error("Patient creation error:", error);
      // toast.error("Failed to add patient: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <DashboardLayout activePage="/patients/add"
    
    // rightcomponent={
    //   <Button variant="outline" onClick={() => navigate("/patients")}>
    //           <ArrowLeft className="w-4 h-4 mr-2" />
    //           Back to Patients
    //         </Button>
    // }
    >
      <div className="space-y-6 sm:space-y-8">
        {/* {isEdit ? (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                Edit Patient
              </h2>
              <p className="text-muted-foreground mt-2">
                Update patient information
              </p>
              {patient?.isOutpatientPatient && (
                <p className="text-sm text-blue-600 mt-1">
                  ℹ️ This is an Outpatient patient - facility cannot be changed
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/patients/${patientId}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Profile
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                Add New Patient
              </h2>
              <p className="text-muted-foreground mt-2">
                Enter patient information to create a new record
              </p>
              {!isAuthorized("canAddPatients") && (
                <p className="text-destructive text-sm mt-2">
                  {!isAllSelected
                    ? "Switch to Anthro Care (All) to add patients"
                    : "Only admins can add patients"}
                </p>
              )}
            </div>
            
          </div>
        )} */}

        {!isAuthorized("canAddPatients") ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Only administrators can add/edit patients
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <LoaderWrapper loading={isEdit && patientLoading} error={isEdit ? patientErr : null}>
            <RHFWrapper
              initialValues={isEdit? patient: {primaryContactPerson: "Provider"}}
              isValidate={true}
              validateSchema={patientValidationSchema}
              onSubmit={handleCreatePatient}
              className="space-y-4 pb-12"
            >
            {/* Organization & Assignment */}
            <Orgcard
              organizations={organizations}
              patient={patient}
              isEdit={isEdit}
            />

            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className={"teachers text-[20px] font-medium "}>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <RhfInput
                    name="firstName"
                    label="First Name"
                    required
                    placeholder="First Name"
                  />
                  <RhfInput
                    name="middleName"
                    label="Middle Name"
                    placeholder="Middle Name"
                  />
                  <RhfInput
                    name="lastName"
                    label="Last Name"
                    required
                    placeholder="Last Name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <DateOfBirthField />
                  <RhfSelect
                    label="Gender"
                    multiple={false}
                    required={true}
                    name="gender"
                    options={[
                      { label: "Male", value: "male" },
                      { label: "Female", value: "female" },
                      { label: "Prefer not to say", value: "others" },
                    ]}
                  />
                  <RhfBatchedInput
                    count={2}
                    label="Height"
                    isNumber
                    // required
                    names={["height_feet", "height_inch"]}
                    maxValue={[8, 11]}
                    batchedLabels={["", ""]}
                    placeholders={["Feet", "Inches"]}
                    rightComponents={[
                      <p className="absolute right-0 top-0 rounded-r-md text-xs text-gray-500 font-semibold bg-gray-200 p-2.5">
                        Ft
                      </p>,
                      <p className="absolute right-0 top-0 rounded-r-md text-xs text-gray-500 font-semibold bg-gray-200 p-2.5">
                        Inch
                      </p>,
                    ]}
                  />
                  {/* <RhfInput
                    name="heightValue"
                    label="Height"
                    // required
                    isNumber
                    placeholder="e.g., 5'10&quot;"
                  /> */}
                  <RhfInput
                    name="weightValue"
                    label="Weight"
                    // required
                    isNumber
                    maxValue={660}
                    placeholder="e.g., 180 lbs"
                    rightComponent={
                      <p className="absolute right-0 top-0 rounded-r-md text-xs text-gray-500 font-semibold bg-gray-200 p-2.5">
                        lbs
                      </p>
                    }
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* <RhfSelect
          label="Type of Patient"
          multiple={false}
          required={true}
          name="typeOfPatient"
          options={[
            { label: "CCM", value: "ccm" },
            { label: "RPM", value: "rpm" },
            { label: "PCM", value: "pcm" },
            { label: "BHI", value: "bhi" },
          ]}
        /> */}
                  {/* <RhfSelect
                    label="Status"
                    multiple={false}
                    required={true}
                    name="status"
                    defaultValue="pending"
                    options={[
                      { label: "Active", value: "active" },
                      { label: "Pending", value: "pending" },
                      { label: "Inactive", value: "inactive" },
                    ]}
                  /> */}
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className={"teachers text-[20px] font-medium "}>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <RhfInput
                    name="email"
                    label="Email"
                    required
                    type="email"
                    placeholder="Email"
                  />
                  <PrimaryPhoneField />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <RhfPhone
                    name="secondaryNumber"
                    label="Secondary Number"
                    placeholder="Secondary Number"
                  />
                  <RhfPhone
                    name="landlineNumber"
                    label="Landline Number"
                    placeholder="Landline Number"
                  />
                </div>

                <div className="space-y-2">
                  <RhfInput
                    name="landlineExtension"
                    label="Landline Extension"
                    placeholder="Ext."
                    isNumber
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <AddressCard />

            {/* Insurance Information */}
            <Card>
              <CardHeader>
                <CardTitle className={"teachers text-[20px] font-medium "}>Insurance Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <RhfInput
                    name="primaryInsuranceName"
                    label="Primary Insurance Name"
                    required
                    placeholder="Primary Insurance Name"
                  />
                  <RhfInput
                    name="primaryInsuranceNumber"
                    label="Primary Insurance Number"
                    required
                    placeholder="Primary Insurance Number"
                    maxLength={20}
                    isAlphanumeric
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <RhfInput
                    name="secondaryInsuranceName"
                    label="Secondary Insurance Name"
                    placeholder="Secondary Insurance Name"
                  />
                  <RhfInput
                    name="secondaryInsuranceNumber"
                    label="Secondary Insurance Number"
                    placeholder="Secondary Insurance Number"
                    maxLength={20}
                    isAlphanumeric
                  />
                </div>
              </CardContent>
            </Card>

            {/* Medical Information */}
            <Card>
              <CardHeader>
                <CardTitle className={"teachers text-[20px] font-medium "}>Medical Information</CardTitle>
              </CardHeader>
              <ICDCodes />
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className={"teachers text-[20px] font-medium "}>Last Visit</CardTitle>
              </CardHeader>
              <CardContent>
                <Grid count={2} gap={3}>
                  <RhfCustomDatepicker
                    name="lastVisit"
                    label="Last Visit"
                    isFutureDateDisabled
                    placeholder="Select last visit date"
                  />
                </Grid>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 fixed bottom-0 bg-white py-4 w-[100%] left-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/patients")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={btnLoading}
                // disabled={}
              >
                {isEdit ? "Update" : "Add"} Patient
              </Button>
            </div>
          </RHFWrapper>
          </LoaderWrapper>
        )}
      </div>
    </DashboardLayout>
  );
}

const Orgcard = ({ organizations, patient, isEdit }) => {
  const { reset, setValue } = useFormContext();

  const selectedOrgForAdd = useWatch({ name: "organizationId" });
  const facilityId = useWatch({ name: "facilityId" });
  const facilityUnit = useWatch({ name: "facilityUnit" });
  const typeOfPatient = useWatch({ name: "typeOfPatient" });
  const assignedProviderId = useWatch({ name: "assignedProviderId" });
  const assignedPractitionerId = useWatch({ name: "assignedPractitionerId" });

  const assignedCaretakerIds = useWatch({ name: "assignedCaretakerIds" });

  // Cache previous valid lists → prevents RHF wiping values
  const [providers, setProviders] = useState([]);
  const [prac, setPrac] = useState([]);
  const [caretakers, setCaretakers] = useState([]);
  const [rooms , setRooms] = useState([]);
  const [bedLocations, setBedLocations] = useState([]);
  const [selectedFacilityData, setSelectedFacilityData] = useState<any>(null);

  const [facilities, setFacilities] = useState([]);
  console.log({ selectedOrgForAdd, typeOfPatient });
  
  // Get organizationId from patient if editing and form hasn't set it yet
  const patientOrgId = isEdit && patient?.organizationId 
    ? (typeof patient.organizationId === 'object' ? patient.organizationId._id : patient.organizationId)
    : null;
  const orgIdForFacility = selectedOrgForAdd || patientOrgId;
  const patientTypeForFacility = typeOfPatient || (isEdit && patient?.typeOfPatient);
  
  const {} = useGetData(
    "/facilities",
    { orgId: orgIdForFacility },
    false,
    !!orgIdForFacility && patientTypeForFacility == "facility",
    (err, data) => {
      if (data && !err) {
        console.log("DATA____", { data: data?.data });
        setFacilities(data.data?.facilities ?? []);
      }
    },
  );
  console.log({ facilities });
  // const facilities =
  //   organizations.find((org) => org?._id == selectedOrgForAdd)?.facilities ||
  //   [];

  // PROVIDERS API
  useGetData(
    "/providers",
    { orgId: selectedOrgForAdd },
    false,
    organizations.length > 0,
    (err, data) => {
      if (!err && data?.data?.providers) {
        setProviders(data.data?.providers ?? []);
        isEdit &&
          setValue(
            "assignedProviderId",
            data.data.providers.some(
              (v) => v?._id == patient?.assignedProviderId,
            )
              ? patient?.assignedProviderId
              : "",
          );
      }
    },
  );

  // CARETAKERS API
  useGetData(
    "/caretakers",
    { orgId: selectedOrgForAdd , limit: 200 },
    false,
    organizations.length > 0,
    (err, data) => {
      if (!err && data?.data?.caretakers) {
        setCaretakers(data.data?.caretakers ?? []);
        if (isEdit) {
          const patientCaretakerIds: string[] = Array.isArray(patient?.assignedCaretakerIds)
            ? patient.assignedCaretakerIds
            : [];

          const hasAnyMatching = data.data.caretakers.some((v: any) =>
            patientCaretakerIds.includes(v?._id),
          );

          setValue(
            "assignedCaretakerIds",
            hasAnyMatching ? patientCaretakerIds : [],
          );
        }
      }
    },
  );

  // Filter caretakers based on patient type
  const filteredCaretakers = useMemo(() => {
    if (!caretakers || caretakers.length === 0) return [];
    
    const currentPatientType = typeOfPatient || (isEdit && patient?.typeOfPatient);
    
    if (currentPatientType === "facility") {
      // If patient type is facility, show only caretakers with type "facility"
      return caretakers.filter((caretaker: any) => caretaker.type === "facility");
    } else {
      // If patient type is NOT facility (outpatient), show caretakers with type "no_facility" or "all"
      return caretakers.filter((caretaker: any) => 
        caretaker.type === "no_facility" || caretaker.type === "all"
      );
    }
  }, [caretakers, typeOfPatient, patient, isEdit]);

  // assignedPractitionerId
  useGetData(
    "/practitioners",
    { providerId: assignedProviderId },
    false,
    organizations.length > 0 && !!assignedProviderId,
    (err, data) => {
      if (!err && data?.data?.practitioners) {
        setPrac(data.data?.practitioners ?? []);
        isEdit &&
          setValue(
            "assignedPractitionerId",
            data.data.practitioners.some(
              (v) => v?._id == patient?.assignedPractitionerId,
            )
              ? patient?.assignedPractitionerId
              : "",
          );
      }
    },
  );

    useGetData(
    "/practitioners",
    { providerId: assignedProviderId },
    false,
    organizations.length > 0 && !!assignedProviderId,
    (err, data) => {
      if (!err && data?.data?.practitioners) {
        setPrac(data.data?.practitioners ?? []);
        isEdit &&
          setValue(
            "assignedPractitionerId",
            data.data.practitioners.some(
              (v) => v?._id == patient?.assignedPractitionerId,
            )
              ? patient?.assignedPractitionerId
              : "",
          );
      }
    },
  );
  // Get facility details when facility is selected
  const patientFacilityId = isEdit && patient?.facilityId
    ? (typeof patient.facilityId === 'object' ? patient.facilityId._id : patient.facilityId)
    : null;
  const currentFacilityId = facilityId || patientFacilityId;
  useGetData("/facilities/" + currentFacilityId, { }, false, organizations.length > 0 && !!currentFacilityId, (err, data)=> {
    if(data && !err){
      setSelectedFacilityData(data?.data);
      // In edit mode, set the unit, room, and position from patient data if available
      if (isEdit && patient?.facilityId && patient?.facilityRoom) {
        // Find the unit that contains the patient's room
        const patientRoom = patient?.facilityRoom;
        const patientBedLocation = patient?.bedLocation;
        const patientUnit = patient?.facilityUnit;
        
        if (patientRoom && data?.data?.units) {
          // First try to use patient's facilityUnit if it exists
          let unitWithRoom = null;
          if (patientUnit) {
            unitWithRoom = data?.data?.units.find((unit: any) => unit.unit === patientUnit);
          }
          
          // If unit not found or doesn't contain the room, search for unit containing the room
          if (!unitWithRoom || !unitWithRoom.rooms?.includes(patientRoom)) {
            unitWithRoom = data?.data?.units.find((unit: any) => 
              unit.rooms && unit.rooms.includes(patientRoom)
            );
          }
          
          if (unitWithRoom) {
            setValue("facilityUnit", unitWithRoom.unit);
            setValue("facilityRoom", patientRoom);
            if (patientBedLocation) {
              setValue("bedLocation", patientBedLocation);
            }
            // Set rooms and bedLocations state for the dropdowns
            setRooms(unitWithRoom.rooms || []);
            setBedLocations(unitWithRoom.bedLocation || []);
          }
        }
      } else if (!isEdit && facilityId) {
        // Clear unit, room, and position when facility changes (only in add mode)
        setValue("facilityUnit", "");
        setValue("facilityRoom", "");
        setValue("bedLocation", "");
        setRooms([]);
        setBedLocations([]);
      }
    }
  });

  // Update rooms and bed locations when unit is selected
  useEffect(() => {
    if (selectedFacilityData && facilityUnit) {
      const selectedUnit = selectedFacilityData?.units?.find((unit: any) => unit.unit === facilityUnit);
      if (selectedUnit) {
        setRooms(selectedUnit.rooms || []);
        setBedLocations(selectedUnit.bedLocation || []);
        
        // In edit mode, preserve patient's room and position if they belong to the selected unit
        if (isEdit && patient?.facilityRoom && selectedUnit.rooms?.includes(patient.facilityRoom)) {
          // Room belongs to this unit, keep it and set position if available
          setValue("facilityRoom", patient.facilityRoom);
          if (patient?.bedLocation && selectedUnit.bedLocation?.includes(patient.bedLocation)) {
            setValue("bedLocation", patient.bedLocation);
          }
        } else if (!isEdit || !patient?.facilityRoom || !selectedUnit.rooms?.includes(patient.facilityRoom)) {
          // Clear room and position when unit changes (add mode or room doesn't belong to this unit)
          setValue("facilityRoom", "");
          setValue("bedLocation", "");
        }
      }
    } else {
      setRooms([]);
      setBedLocations([]);
    }
  }, [facilityUnit, selectedFacilityData, setValue, isEdit, patient?.facilityRoom, patient?.bedLocation]);

  // Clear unit, room, and position when facility changes (only in add mode, not edit mode)
  useEffect(() => {
    if (!facilityId && !isEdit) {
      setSelectedFacilityData(null);
      setValue("facilityUnit", "");
      setValue("facilityRoom", "");
      setValue("bedLocation", "");
      setRooms([]);
      setBedLocations([]);
    }
  }, [facilityId, setValue, isEdit]);

  // Clear selected caretakers when patient type changes and they're no longer valid
  useEffect(() => {
    if (typeOfPatient && assignedCaretakerIds && Array.isArray(assignedCaretakerIds) && assignedCaretakerIds.length > 0) {
      const currentPatientType = typeOfPatient;
      const validCaretakerIds = (filteredCaretakers || []).map((c: any) => c._id);
      const invalidCaretakerIds = assignedCaretakerIds.filter((id: string) => !validCaretakerIds.includes(id));
      
      if (invalidCaretakerIds.length > 0) {
        // Remove invalid caretakers from selection
        const validSelections = assignedCaretakerIds.filter((id: string) => validCaretakerIds.includes(id));
        setValue("assignedCaretakerIds", validSelections);
      }
    }
  }, [typeOfPatient, filteredCaretakers, assignedCaretakerIds, setValue]);



  // console.log({
  //   providers,
  //   caretakers,
  //   selectedOrgForAdd,
  //   assignedCaretakerIds,
  //   assignedProviderId,
  // });

  // console.log({patient});
  // useEffect(()=> {
  //   if(providers.length>0 ){

  //     reset(patient)
  //   }
  // },[providers, caretakers])
  // console.log("ORG", selectedOrgForAdd)
  // useEffect(()=> {
  //   if(!!selectedOrgForAdd){
  //     console.log("ORG CHANGED")
  //         setValue("assignedCaretakerIds","");
  //         setValue("assignedPractitionerId","");
  //         setValue("assignedProviderId","");

  //   }
  //   console.log("ORG CHANGED")
  // },[])

  return (
    <Card>
      <CardHeader>
        <CardTitle className={"teachers text-[20px] font-medium "}>Organization & Assignment</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Organization */}
          <RhfSelect
            label="Organization"
            multiple={false}
            required={true}
            disabled={
              isEdit ||
              !organizations ||
              (Array.isArray(organizations) && organizations.length == 0)
            }
            placeholder={
              isEdit
                ? "Organization cannot be changed"
                : !organizations ||
                  (Array.isArray(organizations) && organizations.length == 0)
                  ? "Add Organization first"
                  : null
            }
            name="organizationId"
            options={
              organizations?.map((org) => ({
                label: org.organizationName,
                value: org._id,
              })) || []
            }
          />

          {/* Facility */}

          <RhfSelect
            label="Patient Type"
            multiple={false}
            required={true}
            name="typeOfPatient"
            options={[
              { label: "Facility", value: "facility" },
              { label: "Outpatient", value: "outpatient" },
            ]}
          />
          {typeOfPatient == "facility" && (
            <RhfSelect
              label="Facility"
              multiple={false}
              required={true}
              disabled={!selectedOrgForAdd || facilities.length == 0}
              placeholder={
                !selectedOrgForAdd
                  ? "Select organization first"
                  : facilities.length == 0
                    ? "No facilities available"
                    : null
              }
              name="facilityId"
              options={(facilities || []).map((facility) => ({
                label: facility.name,
                value: facility._id,
              }))}
            />
          )}
          
          {typeOfPatient == "facility" && (
            <RhfSelect
              label="Unit / Floor"
              multiple={false}
              required={true}
              disabled={!facilityId || !selectedFacilityData || !selectedFacilityData?.units || selectedFacilityData?.units?.length === 0}
              placeholder={
                !facilityId
                  ? "Select facility first"
                  : !selectedFacilityData || !selectedFacilityData?.units || selectedFacilityData?.units?.length === 0
                    ? "No units available"
                    : null
              }
              name="facilityUnit"
              options={selectedFacilityData?.units?.map((unit: any) => ({
                label: `Unit ${unit.unit}`,
                value: unit.unit,
              })) || []}
            />
          )}

          {typeOfPatient == "facility" && (
            <RhfSelect
              label="Room"
              multiple={false}
              required={true}
              disabled={!facilityUnit || rooms.length == 0}
              placeholder={
                !facilityUnit
                  ? "Select unit first"
                  : rooms.length == 0
                    ? "No rooms available"
                    : null
              }
              name="facilityRoom"
              options={(rooms || []).map((room: string) => ({
                label: room,
                value: room,
              }))}
            />
          )}

          {typeOfPatient == "facility" && (
            <RhfSelect
              label="Position"
              multiple={false}
              required={false}
              disabled={!facilityUnit || bedLocations.length == 0}
              placeholder={
                !facilityUnit
                  ? "Select unit first"
                  : bedLocations.length == 0
                    ? "No positions available"
                    : null
              }
              name="bedLocation"
              options={(bedLocations || []).map((location: string) => ({
                label: location,
                value: location,
              }))}
            />
          )}
          {/* Providers */}
          <RhfSelect
            label="Assigned Provider"
            multiple={false}
            required={true}
            defaultvalue={patient?.assignedProviderId}
            name="assignedProviderId"
            placeholder={
              !providers || providers.length == 0
                ? "No providers available"
                : !selectedOrgForAdd
                  ? "Select organization first"
                  : null
            }
            disabled={!providers || providers.length == 0 || !selectedOrgForAdd}
            options={(providers || []).map((provider) => ({
              label: `${provider.firstName} ${provider.lastName}`,
              value: provider._id,
            }))}
          />
          <RhfSelect
            label="Assigned Practitioner"
            multiple={false}
            required={false}
            defaultvalue={patient?.assignedPractitionerId}
            name="assignedPractitionerId"
            placeholder={
              !prac || prac.length == 0
                ? "No Practitoners available"
                : !selectedOrgForAdd
                  ? "Select organization first"
                  : !assignedProviderId
                    ? "Select provider first"
                    : null
            }
            disabled={
              !prac ||
              prac.length == 0 ||
              !selectedOrgForAdd ||
              !assignedProviderId
            }
            options={(prac || []).map((provider) => ({
              label: `${provider.firstName} ${provider.lastName}`,
              value: provider._id,
            }))}
          />

          {/* Caretakers */}
          <RhfSelect
            label="Assigned Caretaker"
            multiple={true}
            defaultvalue={patient?.assignedCaretakerIds}
            required={false}
            placeholder={
              !filteredCaretakers || filteredCaretakers.length == 0
                ? "No caretakers available"
                : !selectedOrgForAdd
                  ? "Select organization first"
                  : !typeOfPatient
                    ? "Select patient type first"
                    : null
            }
            disabled={
              !filteredCaretakers || filteredCaretakers.length == 0 || !selectedOrgForAdd || !typeOfPatient
            }
            name="assignedCaretakerIds"
            options={[
              // { label: "None", value: "none" },
              ...(filteredCaretakers || []).map((caretaker) => ({
                label: `${caretaker.firstName} ${caretaker.lastName}`,
                value: caretaker._id,
              })),
            ]}
          />
          <RhfSelect
          name="primaryContactPerson"
          label="Primary Contact Person"
          required
          options={[
            {label : "Provider", value : "Provider"},
            {label : "Practitioner", value : "Practitioner"}

          ]}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const PrimaryPhoneField = () => {
  const patientType = useWatch({ name: "typeOfPatient" });
  const isOutpatient = patientType === "outpatient";
  return (
    <RhfPhone
      name="primaryNumber"
      label="Phone Number"
      required={isOutpatient}
      placeholder="Phone Number"
    />
  );
};

const AddressCard = () => {
  const patientType = useWatch({ name: "typeOfPatient" });
  if (patientType != "outpatient") return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className={"teachers text-[20px] font-medium "}>Address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Grid count={2} gap={3}>

 <RhfAddressAutoComplete
 key={'address'}
        name="address"
        label="Address"
        onAddressSelect={(data) => {}}
        required
        placeholder="Street, Apartment, etc."
        modifyValuesMapping={{
          address: "address1",
          street: "address2",
          city: "city",
          state: "state",
          zipCode: "zipcode",
        }}
      />
        {/* <RhfInput
          name="address"
          label="Address"
          required
          placeholder="Street, Apartment, etc."
          /> */}
            <RhfInput
          name="street"
          label="Street / Address 2"
          // required
          placeholder="Street, Apartment, etc."
          />
          </Grid>
        <CityStateZipcode
          cityName="city"
          stateName="state"
          zipcodeName="zipCode"
          count={3}
          gap={6}
        />
      </CardContent>
    </Card>
  );
};

const ICDCodes = () => {
  const { control } = useFormContext();
  const pValue = useWatch({ name: "programs" });


  // Watch all diagnosis fields
  const selectedDx = useWatch({
    control,
    name: ["dx1", "dx2", "dx3", "dx4", "dx5"],
  });

  // Flatten selected values
  const chosen = selectedDx.filter(Boolean);

  // Common asyncConfig builder with filter
  const makeAsyncConfig = () => ({
    apiUrl: "https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search",
    searchParam: "terms",
    minSearchLength: 1,
    responseMapper: (response: any) => {
      const list =
        response?.[3]?.map((entry: any[]) => ({
          label: `${entry?.[0]} - ${entry?.[1]}`,
          value: `${entry?.[0]} - ${entry?.[1]}`,
        })) ?? [];

      // 🔥 Filter: remove items already selected in dx1…dx5

      return list.filter((x) => !chosen.includes(x.value));
    },
  });

  return (
    <CardContent className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <RhfAsyncSelect
            label="Diagnosis 1 (Primary)"
            name="dx1"
            isAsync
            asyncConfig={makeAsyncConfig()}
            placeholder="Search for primary diagnosis..."
            required
          />
        </div>

        <RhfAsyncSelect
          label="Diagnosis 2 (Secondary)"
          name="dx2"
          isAsync
          asyncConfig={makeAsyncConfig()}
          placeholder="Search for secondary diagnosis..."
          required={pValue &&  pValue.includes("ccm") ? true : false}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="overflow-hidden">
          <RhfAsyncSelect
            label="Diagnosis 3"
            name="dx3"
            isAsync
            asyncConfig={makeAsyncConfig()}
          />
        </div>
        <div className="overflow-hidden">
          <RhfAsyncSelect
            label="Diagnosis 4"
            name="dx4"
            isAsync
            asyncConfig={makeAsyncConfig()}
          />
        </div>
        <div className="overflow-hidden">
          <RhfAsyncSelect
            label="Diagnosis 5"
            name="dx5"
            isAsync
            asyncConfig={makeAsyncConfig()}
          />
        </div>
      </div>
      <Grid count={1} gap={3}>
        <RhfInput
          // type="textarea"
          name="allergies"
          label="Allergies"
        />
      </Grid>

      <ProgramCard />
    </CardContent>
  );
};

const ProgramCard = () => {
  const pValue = useWatch({ name: "programs" });
  console.log(pValue);

  return (
    <RhfSelect
      name="programs"
      label="Programs"
      required
      multiple
      options={[
        {
          label: "CCM",
          value: "ccm",
          disabled: pValue && pValue.includes("pcm") ? true : false,
        },
        {
          label: "PCM",
          value: "pcm",
          disabled: pValue && pValue.includes("ccm") ? true : false,
        },
        {
          label: "RPM",
          value: "rpm",
          disabled: pValue && pValue.includes("rtm") ? true : false,
        },
        {
          label: "RTM",
          value: "rtm",
          disabled: pValue && pValue.includes("rpm") ? true : false,
        },

        // { label: "BHI", value: "bhi" },
      ]}
      placeholder="e.g., CCM, RPM"
    />
  );
};
